import React, { useState } from "react";
import {
  Bell,
  Equal,
  HelpCircle,
  LogIn,
  LogOut,
  Settings,
  X,
} from "lucide-react";
import { menuData } from "@/config/settings/menu.tsx";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import useAuth from "@integration/user/hooks/useAuth.ts";
import useUser from "@integration/user/hooks/useUser.ts";
import Avatar from "@/components/others/Avatar";
import IconProG from "@/assets/svgComponets/IconProG.tsx";

const MenuDawable: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const { userAuth, avatarFile } = useUser();
  const navigate = useNavigate();

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={`relative mr-2 -ml-3 inline-block text-left`}>
      <motion.button
        onClick={toggleDropdown}
        className="flex items-center justify-center w-10 h-10 bg-gradient-to-b from-gray-800/60 to-gray-900/60 border border-gray-700/50 hover:border-red-500/30 rounded-xl relative overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>

        <Equal
          size={28}
          strokeWidth={1.5}
          className={`absolute transition-all duration-300 ${
            isOpen ? "opacity-0 scale-0" : "opacity-100 scale-100"
          } text-gray-300 hover:text-red-400`}
        />

        <X
          size={28}
          strokeWidth={1.5}
          className={`absolute transition-all duration-300 ${
            isOpen ? "opacity-100 scale-100" : "opacity-0 scale-0"
          } text-red-400`}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-0 left-0 h-screen w-80 bg-gradient-to-b from-gray-900 to-gray-950 text-white shadow-2xl border-r border-gray-700/50 z-50 overflow-x-hidden flex flex-col"
          >
            {/* Decorative elements */}
            <div className="absolute inset-0 bg-[url('/patterns/hexagons.svg')] opacity-5 pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50 pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

            <div className="text-white z-10 h-full flex flex-col">
              {/* Fixed Header */}
              <div className="flex justify-between items-center w-full p-4 border-b border-gray-700/50 bg-gradient-to-b from-gray-800/60 to-gray-900/60 backdrop-blur-sm sticky top-0 z-20">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-red-500/30 to-red-700/30 p-2.5 rounded-xl shadow-md border border-red-500/20">
                    <IconProG  />
                  </div>
                  <span className="font-bold text-xl bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                    ProGamers
                  </span>
                </div>
                <motion.button
                  onClick={toggleDropdown}
                  className="bg-gradient-to-b from-gray-800/60 to-gray-900/60 p-2 rounded-full transition-all border border-gray-700/50 hover:border-red-500/30 text-gray-300 hover:text-red-400"
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X size={20} strokeWidth={1.5} />
                </motion.button>
              </div>

              {/* Profile Section */}
              {isAuthenticated && (
                <div className="flex flex-col items-center px-4 py-6 border-b border-gray-700/50 bg-gradient-to-b from-gray-800/40 to-gray-900/40">
                  <div className="relative mb-3">
                    <Avatar
                      src={avatarFile?.url ?? undefined}
                      sizeClass="w-24 h-24"
                    />

                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {userAuth?.nick || userAuth?.name || "Usuário"}
                  </p>
                  <motion.button
                    onClick={() => {
                      navigate(`/pro/${userAuth?.nick}`);
                      toggleDropdown();
                    }}
                    className="px-4 py-1.5 bg-gradient-to-r from-red-600/20 to-red-700/20 rounded-full text-sm text-white border border-red-500/30 hover:border-red-500/50 transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Ver perfil
                  </motion.button>
                </div>
              )}

              <div className="px-4 mt-4 mb-4">
                <div className="h-0.5 w-full bg-gradient-to-r from-red-500/30 to-transparent"></div>
              </div>

              {/* Non-scrollable section - Dashboard */}
              <div className="flex flex-col px-3 mb-2">
                {isAuthenticated && (
                  <div className="mb-1 text-red-400">
                    <motion.button
                      onClick={() => {
                        navigate("/dashboard");
                        toggleDropdown();
                      }}
                      className="flex items-center gap-4 px-4 py-3 rounded-xl text-sm font-medium text-gray-300 hover:text-white w-full group bg-gradient-to-b from-transparent to-transparent hover:from-gray-800/60 hover:to-gray-900/60 transition-all duration-300 border border-transparent hover:border-gray-700/50 hover:shadow-md relative overflow-hidden"
                      whileHover={{ x: 4, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-2 rounded-lg transition-all group-hover:from-red-500/20 group-hover:to-red-700/20 border border-gray-700/50 group-hover:border-red-500/30 relative z-10">
                        <IconProG />
                      </div>
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3 }}
                        className="z-10 group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-red-300 group-hover:bg-clip-text"
                      >
                        Dashboard
                      </motion.span>
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Scrollable section - Menu items */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-3">
                <div className="space-y-2">
                  {menuData.menu.map((item, index) => (
                    <Link
                      onClick={toggleDropdown}
                      to={item.url}
                      key={index}
                      className="block"
                    >
                      <motion.div
                        className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-sm font-medium text-gray-300 hover:text-white w-full group bg-gradient-to-b from-transparent to-transparent hover:from-gray-800/60 hover:to-gray-900/60 transition-all duration-300 border border-transparent hover:border-gray-700/50 hover:shadow-md relative overflow-hidden"
                        whileHover={{ x: 4, scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-2.5 rounded-lg transition-all group-hover:from-red-500/20 group-hover:to-red-700/20 border border-gray-700/50 group-hover:border-red-500/30 relative z-10">
                          <span className="text-gray-400 group-hover:text-red-400 transition-colors">
                            {item.icon}
                          </span>
                        </div>
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.3 }}
                          className="relative z-10 text-base group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-red-300 group-hover:bg-clip-text group-hover:text-transparent"
                        >
                          {item.label}
                        </motion.span>
                      </motion.div>
                    </Link>
                  ))}
                </div>

                <div className="my-4">
                  <div className="h-0.5 w-full bg-gradient-to-r from-red-500/30 to-transparent"></div>
                </div>

                {isAuthenticated && (
                  <div className="mb-1">
                    <motion.button
                      onClick={() => {
                        navigate("/notifications");
                        toggleDropdown();
                      }}
                      className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-sm font-medium text-gray-300 hover:text-white w-full group bg-gradient-to-b from-transparent to-transparent hover:from-gray-800/60 hover:to-gray-900/60 transition-all duration-300 border border-transparent hover:border-gray-700/50 hover:shadow-md relative overflow-hidden"
                      whileHover={{ x: 4, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-2 rounded-lg transition-all group-hover:from-red-500/20 group-hover:to-red-700/20 border border-gray-700/50 group-hover:border-red-500/30 relative z-10">
                        <Bell
                          size={20}
                          className="text-gray-400 group-hover:text-red-400 transition-colors"
                        />
                      </div>
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3 }}
                        className="z-10 group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-red-300 group-hover:bg-clip-text group-hover:text-transparent"
                      >
                        Notificações
                      </motion.span>
                    </motion.button>
                  </div>
                )}

                {/* Settings and other buttons */}
                {isAuthenticated && (
                  <div className="mt-4 space-y-2">
                    <motion.button
                      onClick={() => {
                        navigate("/settings");
                        toggleDropdown();
                      }}
                      className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-sm font-medium text-gray-300 hover:text-white w-full group bg-gradient-to-b from-transparent to-transparent hover:from-gray-800/60 hover:to-gray-900/60 transition-all duration-300 border border-transparent hover:border-gray-700/50 hover:shadow-md relative overflow-hidden"
                      whileHover={{ x: 4, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-2 rounded-lg transition-all group-hover:from-red-500/20 group-hover:to-red-700/20 border border-gray-700/50 group-hover:border-red-500/30 relative z-10">
                        <Settings
                          size={20}
                          className="text-gray-400 group-hover:text-red-400 transition-colors"
                        />
                      </div>
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3 }}
                        className="relative z-10 group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-red-300 group-hover:bg-clip-text group-hover:text-transparent"
                      >
                        Configurações
                      </motion.span>
                    </motion.button>

                    {/* Hidden support button */}
                    <div className="hidden">
                      <motion.button
                        onClick={() => {
                          navigate("/support");
                          toggleDropdown();
                        }}
                        className="flex items-center gap-4 px-4 py-3 rounded-xl text-sm font-medium text-gray-300 hover:text-white w-full group bg-gradient-to-b from-transparent to-transparent hover:from-gray-800/60 hover:to-gray-900/60 transition-all duration-300 border border-transparent hover:border-gray-700/50 hover:shadow-md relative overflow-hidden"
                        whileHover={{ x: 4, scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-2 rounded-lg transition-all group-hover:from-red-500/20 group-hover:to-red-700/20 border border-gray-700/50 group-hover:border-red-500/30 relative z-10">
                          <HelpCircle
                            size={20}
                            className="text-gray-400 group-hover:text-red-400 transition-colors"
                          />
                        </div>
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.3 }}
                          className="relative z-10 group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-red-300 group-hover:bg-clip-text group-hover:text-transparent"
                        >
                          Suporte
                        </motion.span>
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Logout button for authenticated users */}
                {isAuthenticated && (
                  <div className="mt-auto pt-6 pb-6">
                    <motion.button
                      onClick={() => {
                        logout();
                        toggleDropdown();
                      }}
                      className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-sm font-medium text-gray-300 hover:text-white w-full group bg-gradient-to-b from-transparent to-transparent hover:from-red-600/20 hover:to-red-700/20 transition-all duration-300 border border-transparent hover:border-red-500/30 hover:shadow-md relative overflow-hidden"
                      whileHover={{ x: 4, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-2 rounded-lg transition-all group-hover:from-red-500/30 group-hover:to-red-700/30 border border-gray-700/50 group-hover:border-red-500/50 relative z-10">
                        <LogOut
                          size={20}
                          className="text-gray-400 group-hover:text-red-400 transition-colors"
                        />
                      </div>
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3 }}
                        className="relative z-10 group-hover:text-red-400"
                      >
                        Sair
                      </motion.span>
                    </motion.button>
                  </div>
                )}

                {/* Login button for non-authenticated users */}
                {!isAuthenticated && (
                  <div className="mt-auto pt-6 pb-6">
                    <motion.button
                      onClick={() => {
                        navigate("/login");
                        toggleDropdown();
                      }}
                      className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-sm font-medium text-gray-300 hover:text-white w-full group bg-gradient-to-b from-transparent to-transparent hover:from-red-600/20 hover:to-red-700/20 transition-all duration-300 border border-transparent hover:border-red-500/30 hover:shadow-md relative overflow-hidden"
                      whileHover={{ x: 4, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-2 rounded-lg transition-all group-hover:from-red-500/30 group-hover:to-red-700/30 border border-gray-700/50 group-hover:border-red-500/50 relative z-10">
                        <LogIn
                          size={20}
                          className="text-gray-400 group-hover:text-red-400 transition-colors"
                        />
                      </div>
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3 }}
                        className="relative z-10 group-hover:text-red-400"
                      >
                        Login
                      </motion.span>
                    </motion.button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuDawable;
